import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { Bar, Pie } from 'react-chartjs-2';
import SelectMetric from './SelectMetric.js';
import { DataContext } from './DataContextProvider.js';
import COLORS from './colors.js';

export default function PieChart({ item, numSlices, filters, withCount, ignoreNoneOption, asBarChart, barScale, fixedData, fixedMetric, fixedSeries, fixedNumSlices, title }) {
  const [selectedMetric, setSelectedMetric] = React.useState(fixedMetric || -1);
  const [barChart, setBarChart] = React.useState(asBarChart || localStorage.getItem('useBarChart') === 'true');
  const dataContext = React.useContext(DataContext);
  const dataAvailable = fixedData ? true : dataContext.getItemData({
    start: dataContext.start,
    end: dataContext.end,
    rows: item,
    filters: filters,
  });
  if (barChart) {
    numSlices = 35;
  }
  else if (!numSlices) {
    numSlices = 10;
  }
  if (fixedNumSlices) {
    numSlices = fixedNumSlices;
  }

  let chartData = {};
  if (dataAvailable && selectedMetric !== -1) {
    const metric = (!!fixedMetric) ? ['users', 'sessions', 'page_views', 'organic_views', 'direct_views', 'paid_views', 'signups', 'rate'].indexOf(fixedMetric) + 1 : selectedMetric;
    let allData = fixedData ? fixedData : [...dataContext.itemData.results];
    let data;
    if (!fixedSeries) {
      allData = allData.sort((a, b) => b[metric] - a[metric]);
      data = allData;
    }
    else {
      let n = allData.length - 1;
      const indexes = Object.keys(allData[n]);
      const sortedIndexes = indexes.sort((a, b) => allData[n][b][metric] - allData[n][a][metric]);
      allData = allData.map(d => sortedIndexes.map(i => d[i]));
      data = allData[0];
    }

    const getChartData = (data) => {
      const result = {labels: [], datasets: []};
      if (ignoreNoneOption !== false && dataContext.ignoreNone) {
        data = data.filter(x => x[0][1] !== 'None' && x[0][1] !== ignoreNoneOption);
      }
      let others = data.slice(numSlices - 1).reduce((a, b) => a + b[metric], 0);
      let othersPosts = data.slice(numSlices - 1).reduce((a, b) => a + b[6], 0);
      let total = data.reduce((a, b) => a + b[metric], 0);
      data = data.slice(0, numSlices - 1)

      switch (barScale) {
        case 'posts':
          result.datasets.push({data: data.map(x => x[metric] / x[6])});
          break;
        case 'total':
          result.datasets.push({data: data.map(x => parseInt(1000.0 * x[metric] / total) / 10.0)});
          break;
        default:
          result.datasets.push({data: data.map(x => x[metric])});
          break;
      }
      result.labels = data.map(x => x[0][1]);
      if (others !== 0) {
        switch (barScale) {
          case 'posts':
            result.datasets[0].data.push(others / othersPosts);
            break;
          case 'total':
            result.datasets[0].data.push(parseInt(1000.0 * others / total) / 10.0);
            break;
          default:
            result.datasets[0].data.push(others);
            break;
        }
        result.labels.push('All others')
      }
      result.datasets[0].backgroundColor = [];
      result.datasets[0].borderColor = [];
      for (let i = 0; i < result.datasets[0].data.length; i++) {
        result.datasets[0].backgroundColor.push(COLORS[i % COLORS.length]);
        result.datasets[0].borderColor.push('#ffffff');
      }
      return result;
    };

    chartData = getChartData(data);
    if (barChart) {
      for (let i = 0; i < chartData.datasets[0].data.length; i++) {
        chartData.datasets[0].backgroundColor[i] = COLORS[0];
        chartData.datasets[0].borderColor[i] = COLORS[0];
      }

      if (fixedSeries) {
        // there are additional series in this chart
        chartData.datasets[0].label = fixedSeries[0];
        for (let i = 1; i < fixedSeries.length; i++) {
          data = allData[i];
          const dataset = {
            label: fixedSeries[i],
            data: getChartData(data).datasets[0].data,
            backgroundColor: COLORS[i % COLORS.length],
            borderColor: COLORS[i % COLORS.length],
          }
          chartData.datasets = [...chartData.datasets, dataset];
        }
      }
    }
  }

  const usePieChart = () => {
    setBarChart(false);
    localStorage.setItem('useBarChart', false);
  };

  const useBarChart = () => {
    setBarChart(true);
    localStorage.setItem('useBarChart', true);
  };

  let options = barChart ? {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: (chartData.datasets && chartData.datasets.length > 1),
        position: 'top',
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  } : {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  return (
    <div className={barChart ? 'card line-chart-tall' : 'card pie-chart'}>
      {title &&
        <Row><Col><h4>{title}</h4></Col></Row>
      }
      { dataAvailable ?
        <>
          {!fixedMetric &&
            <Container fluid>
              <Row>
                <Col>
                  <SelectMetric changeMetric={setSelectedMetric} withCount={withCount} toggleNone={ignoreNoneOption} name="pie" />
                </Col>
                <Col className="text-end">
                  <ButtonGroup>
                    <Button variant={barChart ? 'outline-secondary' : 'primary'} size="sm" onClick={usePieChart}>Pie</Button>
                    <Button variant={barChart ? 'primary' : 'outline-secondary'} size="sm" onClick={useBarChart}>Bar</Button>
                  </ButtonGroup>
                </Col>
              </Row>
            </Container>
          }
          {chartData.labels &&
            <>
              {barChart ?
                <div className="barChartDiv">
                  <Bar data={chartData} options={options} />
                </div>
              :
                <div className="pieChartDiv">
                  <Pie data={chartData} options={options} />
                </div>
              }
            </>
          }
        </>
      :
        <Spinner animation="grow" />
      }
    </div>
  );
}
